/* Phase 10.8A-4.3 · Trust Signal Strip
 *
 * A marketing surface that renders capability metrics ("what the
 * platform enforces") by default, and — once LI_TRUST_SIGNAL_DYNAMIC
 * is enabled on the backend — pivots to real usage counts.
 *
 * Compact horizontal strip. Suitable for the SundayBuilder™ marketing
 * page, cathedral demo, and diocesan outreach one-pager.
 */
import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrustSignalStrip({ compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/intelligence/trust-signal`);
        if (!r.ok) throw new Error(r.statusText);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="trust-signal-strip trust-signal-loading"
           data-testid="trust-signal-loading" style={_wrapStyle(compact)}>
        <span style={_taglineStyle}>Every recommendation has a reason.</span>
      </div>
    );
  }

  const items = data.version === "v2" && data.dynamic
    ? _dynamicItems(data.dynamic)
    : data.labels;

  return (
    <div className={`trust-signal-strip trust-signal-${data.version}`}
         data-testid="trust-signal-strip"
         style={_wrapStyle(compact)}>
      <div style={_headerStyle}>
        <span style={_brandStyle}>Powered by Ordo AI™</span>
        <span style={_taglineStyle}>{data.tagline}</span>
      </div>
      <ul style={_listStyle(compact)}>
        {items.map((label, i) => (
          <li key={i} style={_itemStyle}
              data-testid={`trust-signal-item-${i}`}>
            <span style={_checkStyle}>✓</span>
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function _dynamicItems(d) {
  const items = [];
  if (d.worship_plans_generated) {
    items.push(`${_num(d.worship_plans_generated)} recommendations analyzed`);
  }
  items.push(`${d.citation_backed_pct}% citation-backed`);
  items.push(`${d.mandatory_violations_persisted} mandatory rubric violations`);
  items.push("Every decision auditable");
  return items;
}


function _num(n) {
  try { return Number(n).toLocaleString(); } catch { return String(n); }
}


const _wrapStyle = (compact) => ({
  background: "linear-gradient(180deg, #22343f 0%, #1a2830 100%)",
  color: "#f7f1e5",
  padding: compact ? "10px 16px" : "18px 24px",
  borderRadius: 8,
  fontFamily: "'EB Garamond', Georgia, serif",
  boxShadow: "0 4px 18px rgba(34,52,63,0.20)",
});

const _headerStyle = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 10,
  flexWrap: "wrap",
};

const _brandStyle = {
  fontSize: 12,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "#c9963a",
  fontWeight: 700,
};

const _taglineStyle = {
  fontSize: 14,
  fontStyle: "italic",
  color: "#f7f1e5",
};

const _listStyle = (compact) => ({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "grid",
  gridTemplateColumns: compact ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
  gap: compact ? 4 : 8,
});

const _itemStyle = {
  display: "flex",
  gap: 8,
  alignItems: "baseline",
  fontSize: 14.5,
};

const _checkStyle = {
  color: "#c9963a",
  fontWeight: 700,
  fontSize: 16,
};
